import { cva } from 'class-variance-authority';

import { buttonVariants } from '@/hen-ui/atomics/Button/button.variants';
import type {
  PillButtonColor,
  PillButtonHover,
  PillButtonPadding,
  PillButtonRounded,
  PillButtonSize,
  PillButtonVariant,
  PillButtonWeight,
} from './pill-button.options';

/**
 * PillButton classes: Button's structure (focus ring, press scale,
 * transitions, disabled and busy states) with no Button color or size
 * variant, and the pill's type, spacing, shape and colors from the
 * `--cds-pill-button-*` tokens in `constants/pillButtonTheme.ts`.
 *
 * `h-auto` replaces the shadcn base `h-10`, so the height comes from the
 * line height and padding. A pill stays as wide as its one-line label.
 *
 * Each option sets those same variables on the element, so the token
 * utilities still do the styling and an unset option leaves the tenant's
 * tokens in charge. `padding` is declared after `size`, so `cn` keeps its
 * padding values over the size's. `hover` changes only the hover color of a
 * style and color pair: `lighter` is that pair's own hover, and its entries
 * follow the pair's so `cn` keeps them. With no style or color, `lighter`
 * leaves the tenant's hover, `darker` uses the neutral darker shade, and
 * `none` keeps the resting color.
 *
 * The border is an inset shadow so it sits inside the padding and the pill
 * stays the same height in every style. It reads
 * `--cds-pill-button-color-border`: with no style chosen there is no border
 * until a tenant sets it, the outline falls back to the text color (neutral
 * outline sets neutral 200), and filled and ghost clear it.
 *
 * Color values are written out with their palette fallback so the classes
 * compile the same in every consumer config; the affiliate-bundle config has
 * no `on-fill` colors. A token test checks them against the hen-ui palette.
 */
export const pillButtonVariants = cva(
  [
    buttonVariants({ variant: null, size: null }),
    'h-auto shrink-0 whitespace-nowrap',
    'font-pill-button text-pill-button',
    'px-pill-button-x py-pill-button-y gap-pill-button rounded-pill-button',
    'bg-pill-button text-pill-button-fg',
    'hover:bg-pill-button-hover active:bg-pill-button-active aria-pressed:bg-pill-button-active',
    'shadow-[inset_0_0_0_1px_var(--cds-pill-button-color-border,transparent)]',
    '[&_svg]:size-pill-button-icon [&_[data-hen-icon]]:size-pill-button-icon',
  ],
  {
    variants: {
      weight: {
        regular: '[--cds-pill-button-font-weight:400]',
        medium: '[--cds-pill-button-font-weight:500]',
        semibold: '[--cds-pill-button-font-weight:600]',
        bold: '[--cds-pill-button-font-weight:700]',
      } satisfies Record<PillButtonWeight, string>,
      size: {
        sm: [
          '[--cds-pill-button-font-size:var(--font-size-caption,12px)]',
          '[--cds-pill-button-line-height:var(--line-height-caption,18px)]',
          '[--cds-pill-button-padding-x:var(--space-xxs,8px)]',
          '[--cds-pill-button-padding-y:var(--space-min,4px)]',
          '[--cds-pill-button-icon-size:0.875rem]',
        ],
        md: [
          '[--cds-pill-button-font-size:var(--font-size-b5,14px)]',
          '[--cds-pill-button-line-height:1.3125rem]',
          '[--cds-pill-button-padding-x:var(--space-xxs,8px)]',
          '[--cds-pill-button-padding-y:var(--space-xxs,8px)]',
          '[--cds-pill-button-icon-size:1rem]',
        ],
      } satisfies Record<PillButtonSize, string[]>,
      variant: {
        filled: '[--cds-pill-button-color-border:transparent]',
        outline:
          'shadow-[inset_0_0_0_1px_var(--cds-pill-button-color-border,currentColor)]',
        ghost: '[--cds-pill-button-color-border:transparent]',
      } satisfies Record<PillButtonVariant, string>,
      color: {
        neutral: '',
        primary: '',
        secondary: '',
        accent: '',
      } satisfies Record<PillButtonColor, string>,
      rounded: {
        full: '[--cds-pill-button-radius:var(--radius-max,100px)]',
        sm: '[--cds-pill-button-radius:var(--radius-sm,8px)]',
        none: '[--cds-pill-button-radius:0px]',
      } satisfies Record<PillButtonRounded, string>,
      padding: {
        sm: [
          '[--cds-pill-button-padding-x:var(--space-xxs,8px)]',
          '[--cds-pill-button-padding-y:var(--space-min,4px)]',
        ],
        md: [
          '[--cds-pill-button-padding-x:var(--space-xs,12px)]',
          '[--cds-pill-button-padding-y:var(--space-xxs,8px)]',
        ],
        lg: [
          '[--cds-pill-button-padding-x:var(--space-sm,16px)]',
          '[--cds-pill-button-padding-y:var(--space-xs,12px)]',
        ],
      } satisfies Record<PillButtonPadding, string[]>,
      hover: {
        lighter: '',
        darker:
          '[--cds-pill-button-color-background-hover:var(--neutral-100,#E6E9EC)]',
        none: 'hover:bg-pill-button',
      } satisfies Record<PillButtonHover, string>,
    },
    compoundVariants: [
      {
        variant: 'filled',
        color: 'neutral',
        className: [
          '[--cds-pill-button-color-background:var(--neutral-900,#1C1E21)]',
          '[--cds-pill-button-color-background-hover:var(--neutral-700,#30333A)]',
          '[--cds-pill-button-color-background-active:var(--neutral-black,#000000)]',
          '[--cds-pill-button-color-text:var(--neutral-on-fill,var(--neutral-white,#FFFFFF))]',
        ],
      },
      {
        variant: 'filled',
        color: 'primary',
        className: [
          '[--cds-pill-button-color-background:var(--primary-700,#005BF2)]',
          '[--cds-pill-button-color-background-hover:var(--primary-500,#4487F5)]',
          '[--cds-pill-button-color-background-active:var(--primary-900,#002F7D)]',
          '[--cds-pill-button-color-text:var(--primary-on-fill,var(--neutral-white,#FFFFFF))]',
        ],
      },
      {
        variant: 'filled',
        color: 'secondary',
        className: [
          '[--cds-pill-button-color-background:var(--secondary-700,#7C46DA)]',
          '[--cds-pill-button-color-background-hover:var(--secondary-500,#9F78E4)]',
          '[--cds-pill-button-color-background-active:var(--secondary-900,#3E01A8)]',
          '[--cds-pill-button-color-text:var(--secondary-on-fill,var(--neutral-white,#FFFFFF))]',
        ],
      },
      {
        variant: 'filled',
        color: 'accent',
        className: [
          '[--cds-pill-button-color-background:var(--accent-700,#FC3F80)]',
          '[--cds-pill-button-color-background-hover:var(--accent-500,#FC72A1)]',
          '[--cds-pill-button-color-background-active:var(--accent-900,#D2034A)]',
          '[--cds-pill-button-color-text:var(--accent-on-fill,var(--neutral-black,#000000))]',
        ],
      },
      {
        variant: 'outline',
        className:
          '[--cds-pill-button-color-background:var(--neutral-white,#FFFFFF)]',
      },
      {
        variant: 'ghost',
        className: '[--cds-pill-button-color-background:transparent]',
      },
      {
        variant: 'outline',
        color: 'neutral',
        className:
          '[--cds-pill-button-color-border:var(--neutral-200,#B1B8C3)]',
      },
      {
        variant: ['outline', 'ghost'],
        color: 'neutral',
        className: [
          '[--cds-pill-button-color-background-hover:var(--neutral-50,#F6F8FB)]',
          '[--cds-pill-button-color-background-active:var(--neutral-100,#E6E9EC)]',
          '[--cds-pill-button-color-text:var(--neutral-900,#1C1E21)]',
        ],
      },
      {
        variant: ['outline', 'ghost'],
        color: 'primary',
        className: [
          '[--cds-pill-button-color-background-hover:var(--primary-50,#EDF4FF)]',
          '[--cds-pill-button-color-background-active:var(--primary-100,#D8E6FD)]',
          '[--cds-pill-button-color-text:var(--primary-700,#005BF2)]',
        ],
      },
      {
        variant: ['outline', 'ghost'],
        color: 'secondary',
        className: [
          '[--cds-pill-button-color-background-hover:var(--secondary-50,#F5F0FE)]',
          '[--cds-pill-button-color-background-active:var(--secondary-100,#E4D9F8)]',
          '[--cds-pill-button-color-text:var(--secondary-700,#7C46DA)]',
        ],
      },
      {
        variant: ['outline', 'ghost'],
        color: 'accent',
        className: [
          '[--cds-pill-button-color-background-hover:var(--accent-50,#FFECF3)]',
          '[--cds-pill-button-color-background-active:var(--accent-100,#FFD4E3)]',
          '[--cds-pill-button-color-text:var(--accent-900,#D2034A)]',
        ],
      },
      {
        variant: 'filled',
        color: 'neutral',
        hover: 'darker',
        className:
          '[--cds-pill-button-color-background-hover:var(--neutral-black,#000000)]',
      },
      {
        variant: 'filled',
        color: 'primary',
        hover: 'darker',
        className:
          '[--cds-pill-button-color-background-hover:var(--primary-900,#002F7D)]',
      },
      {
        variant: 'filled',
        color: 'secondary',
        hover: 'darker',
        className:
          '[--cds-pill-button-color-background-hover:var(--secondary-900,#3E01A8)]',
      },
      {
        variant: 'filled',
        color: 'accent',
        hover: 'darker',
        className:
          '[--cds-pill-button-color-background-hover:var(--accent-900,#D2034A)]',
      },
      {
        variant: 'filled',
        color: 'neutral',
        hover: 'none',
        className:
          '[--cds-pill-button-color-background-hover:var(--neutral-900,#1C1E21)]',
      },
      {
        variant: 'filled',
        color: 'primary',
        hover: 'none',
        className:
          '[--cds-pill-button-color-background-hover:var(--primary-700,#005BF2)]',
      },
      {
        variant: 'filled',
        color: 'secondary',
        hover: 'none',
        className:
          '[--cds-pill-button-color-background-hover:var(--secondary-700,#7C46DA)]',
      },
      {
        variant: 'filled',
        color: 'accent',
        hover: 'none',
        className:
          '[--cds-pill-button-color-background-hover:var(--accent-700,#FC3F80)]',
      },
      {
        variant: ['outline', 'ghost'],
        color: 'neutral',
        hover: 'darker',
        className:
          '[--cds-pill-button-color-background-hover:var(--neutral-100,#E6E9EC)]',
      },
      {
        variant: ['outline', 'ghost'],
        color: 'primary',
        hover: 'darker',
        className:
          '[--cds-pill-button-color-background-hover:var(--primary-100,#D8E6FD)]',
      },
      {
        variant: ['outline', 'ghost'],
        color: 'secondary',
        hover: 'darker',
        className:
          '[--cds-pill-button-color-background-hover:var(--secondary-100,#E4D9F8)]',
      },
      {
        variant: ['outline', 'ghost'],
        color: 'accent',
        hover: 'darker',
        className:
          '[--cds-pill-button-color-background-hover:var(--accent-100,#FFD4E3)]',
      },
      {
        variant: 'outline',
        hover: 'none',
        className:
          '[--cds-pill-button-color-background-hover:var(--neutral-white,#FFFFFF)]',
      },
      {
        variant: 'ghost',
        hover: 'none',
        className: '[--cds-pill-button-color-background-hover:transparent]',
      },
    ],
  },
);
